// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getColor from "@insite/mobius/utilities/getColor";
import { screen } from "@rugby/XDL/Helpers";
import { color, ellipsis } from "@rugby/XDL/Utilities";
import { styledScrollbar } from "@rugby/XDL/Utilities/SharedStyles";
import styled, { css } from "styled-components";

const StyledOverlay = styled.div<any>`
    visibility: hidden;
    opacity: 0;
    transition: opacity 450ms ease-out, visibility 450ms ease-out;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    position: fixed;
    background-color: ${color("text.main", 0.71)};
    width: 0;
    z-index: 8;

    ${({ isVisible }) => {
        if (isVisible) {
            return `
            visibility: visible;
            opacity: 1;
            width: auto;`;
        }
    }};
`;

const StyledLinkWrapper = styled.div<any>`
    &&.menu-opened p {
        color: ${getColor("primary")};
    }
`;

const StyledMenu = styled.div<any>`
    width: ${({ fullWidth }) => (fullWidth ? "506px" : "250px")};
    max-width: 30vw;
    height: 100%;
    ${({ fullWidth }) => fullWidth && screen("gte", "md")`box-shadow:0 2px 7px 0 ${color("common.black.main", 0.2)};`};

    .overflow-wrapper {
        display: flex;
        flex-direction: column;
        align-items: ${({ fullWidth }) => (fullWidth ? "center" : "flex-start")};
        justify-content: flex-start;
        height: 100%;
        background: white;
    }

    .scroll-wrapper {
        overflow-y: auto;
        overflow-x: hidden;
        width: 100%;
        height: 100%;
        padding-top: 40px;
        padding-left: ${({ fullWidth }) => (fullWidth ? "120px" : "30px")};
        ${styledScrollbar()}
    }

    &.sub-menu {
        width: ${({ fullWidth }) => (fullWidth ? "425px" : "250px")};
        position: absolute;
        top: 0;
        left: -100%;
        height: 100%;
        visibility: hidden;
        pointer-events: none;

        & > .overflow-wrapper {
            align-items: flex-start;
        }

        & > .overflow-wrapper > .scroll-wrapper {
            padding-left: 30px;
            ${screen("gte", "md")`
                box-shadow: inset 0 0 7px 0 rgba(0,73,144,0.15);
            `};

            & > div {
                transition: opacity 350ms ease-out, transform 350ms ease-out;
                transform: translateX(-100%);
                opacity: 0;
            }
        }

        &.menu-opened {
            left: 100%;
            visibility: visible;
            pointer-events: auto;
            z-index: 2;

            & > .overflow-wrapper > .scroll-wrapper > div {
                transform: none;
                opacity: 1;
            }
        }
    }

    ${screen("lte", "sm")`
        max-width: none;
        &, &.sub-menu {
            width: 100%;
            height: auto;
            position: fixed;
            top: 75px;
            bottom: 0;
        }
        &.sub-menu.menu-opened {
            left: 0;
        }

        .scroll-wrapper, &.sub-menu > .overflow-wrapper > .scroll-wrapper{
           padding:0;
           background: linear-gradient(rgba(80,106,113,0.07), rgba(80,106,113,0.07)), linear-gradient(white, white);
        }

        .menu-separator {
            height: 10px;
            background: rgba(80,106,113,0.07);
            margin: 20px -20px;
        }

        .scroll-wrapper > div {

            background-color: white;
        }

        .scroll-wrapper, .overflow-wrapper {
            width: 90vw;
            height: 100%;
            max-width: 100%;
        }
    `}
`;

const StyledMenuHeaderTitle = {
    css: css`
        ${ellipsis(1, 27)}
    `,
};

const StyledMenuItem = styled.div`
    text-align: left;
    padding: 10px 10px 10px 0;
    display: flex;
    align-items: center;

    &&& span {
        color: ${getColor("secondary")};
        font-size: 15px;
        letter-spacing: 0;
        line-height: 18px;
        transition: color 250ms ease-in, opacity 250 ease-in;
    }

    & > a,
    & > span {
        min-width: 0;
        & > span {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    &&&:hover > a > span,
    &&&:hover > span > span {
        color: ${getColor("text.main")};
    }

    .link-chevron {
        opacity: 0;
        height: 100%;
        margin-left: 5px;
        flex-shrink: 0;
    }

    .link-chevron.chevron-visible {
        opacity: 1;
    }

    .icon-link > span {
        display: flex;
        align-items: center;
        .link-icon {
            margin-right: 5px;
            ${screen("gt", "sm")`
                display: none;
            `}
        }
    }

    ${screen("lte", "sm")`
        margin-bottom: 15px;
        padding: 10px 15px;
        width: 100%;
        justify-content: space-between;
        &:last-child {
            margin-bottom:0;
        }
        &&& span {
            font-size: 18px;
            line-height: 25px;
        }

        .link-chevron {
            opacity: 1;
            svg {
                width:16px;
                height:16px;
            }
        }
    `}
`;

const StyledTooltip = styled.div<any>`
    ${({ isVisible }) => (isVisible ? "display:flex;" : "display:none;")};
    opacity: 0;
    position: fixed;
    top: 100%;
    min-width: 200px;
    min-height: 200px;
    ${({ fullWidth }) => (fullWidth ? `width:100%` : "")};
    max-height: 85vh;
    background-color: white;
    transition: opacity 350ms ease-in;
    will-change: transform;
    top: 0;
    left: 0;
    z-index:9;
    ${({ arrow }) => {
        if (arrow) {
            return `
                    box-shadow: 0 0 15px 0 rgba(80, 106, 113, 0.25);
                    z-index:11;
                    //overflow: visible;
                    .arrow {
                        border-right: solid 15px transparent;
                        border-left: solid 15px transparent;
                        border-bottom: solid 15px white;
                        position:absolute;
                        top: -15px;
                        left: 0;
                        will-change: transform;
                    }`;
        }
    }}

    ${({ isHeaderMenu }) => {
        if (isHeaderMenu) {
            return screen("lte", "sm")`
                top:0;
                left:0;
                bottom:0;
                height: -webkit-fill-available;
                max-height: none;
                position:fixed;
                flex-direction:column;
                width:90vw;
                max-width: none;
                z-index:11;
                transform: none !important;
                max-width: 422px !important;
            `;
        }
    }}

    ${({ isHeaderMenu }) => {
        if (isHeaderMenu) {
            return `
                height: auto;
                min-height: 546px;
                max-height: 546px;
            `;
        }
    }}

    ${screen("lte", "md")`
         .menu-image {
            display:none;
          }
    `}

    ${screen("lte", "sm")`
        min-height: 0;
    `}

    &.recentlyPurchased-menu {
      ${({ isHeaderMenu }) => {
          if (isHeaderMenu) {
              return `
                max-height: 615px;
            `;
          }
      }}
    }
`;

const StyledMenuHeader = styled.div`
    display: none;
    ${screen("lte", "sm")`
        width: 100%;
        height: 75px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        background-color: #007ab8;
        padding: 20px;
        font-size: 18px;

        &,
        & button {
            color: white;
        }
    `}
`;

export const mainNavigationImageSideStyles = {
    container: {
        css: css`
            flex: 1;
            width: 100%;
        `,
    },
    clickableContainer: {
        css: css`
            width: 100%;
            height: 100%;
            display: flex;
        `,
    },
    image: {
        css: css`
            width: 100%;
            max-width: 100%;
            max-height: 100%;

            img {
            }
        `,
    },
};

const StyledMenuHeaderAddress = {
    css: css`
        margin-left: 5px;
        ${screen("lte", "xs")`
            font-size: 15px;
            margin-left: 0;
        `}
    `,
};

const StyledMenuHeaderText = {
    css: css`
        display: flex;
        align-items: center;
        ${screen("lte", "xs")`
            flex-direction: column;
            align-items: flex-start;
        `}
    `,
};

export {
    StyledOverlay,
    StyledLinkWrapper,
    StyledMenuHeader,
    StyledMenuHeaderTitle,
    StyledTooltip,
    StyledMenu,
    StyledMenuItem,
    StyledMenuHeaderAddress,
    StyledMenuHeaderText,
};
